// @ts-nocheck
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRightIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  ExclamationCircleIcon,
  LifebuoyIcon,
} from "@heroicons/react/24/outline";
import { URL } from "../../service/Service";

const STATUS_LABELS = {
  OPEN: "Aberto",
  IN_PROGRESS: "Em análise",
  WAITING_CUSTOMER: "A aguardar a sua resposta",
  RESOLVED: "Resolvido",
  CLOSED: "Fechado",
};

const STATUS_STYLES = {
  OPEN: "bg-blue-50 text-blue-700",
  IN_PROGRESS: "bg-amber-50 text-amber-700",
  WAITING_CUSTOMER: "bg-violet-50 text-violet-700",
  RESOLVED: "bg-emerald-50 text-emerald-700",
  CLOSED: "bg-slate-100 text-slate-600",
};

export default function PersonalSupportSummary() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const load = () =>
      fetch(`${URL}/api/support/tickets`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("jwt") || ""}` },
      })
        .then((response) => (response.ok ? response.json() : []))
        .then((data) => active && setTickets(Array.isArray(data) ? data : []))
        .catch(() => active && setTickets([]))
        .finally(() => active && setLoading(false));

    void load();
    const refresh = window.setInterval(load, 30000);
    window.addEventListener("support-notifications-updated", load);
    return () => {
      active = false;
      window.clearInterval(refresh);
      window.removeEventListener("support-notifications-updated", load);
    };
  }, []);

  const activeTickets = useMemo(
    () => tickets.filter((ticket) => !["RESOLVED", "CLOSED"].includes(ticket.status)),
    [tickets],
  );
  const waitingForTeam = activeTickets.filter((ticket) =>
    ["OPEN", "IN_PROGRESS"].includes(ticket.status),
  ).length;
  const waitingForCustomer = activeTickets.filter(
    (ticket) => ticket.status === "WAITING_CUSTOMER",
  ).length;
  const recent = [...tickets]
    .sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt))
    .slice(0, 3);

  return (
    <section className="mt-6 overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-sm">
      <div className="flex flex-col gap-4 border-b border-slate-100 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700">
            <LifebuoyIcon className="h-6 w-6" />
          </span>
          <div>
            <h2 className="text-xl font-extrabold text-slate-950">Os meus pedidos de suporte</h2>
            <p className="mt-1 text-sm text-slate-500">Acompanhe respostas da administração sem sair do dashboard.</p>
          </div>
        </div>
        <Link to="/support" className="inline-flex items-center gap-1.5 text-sm font-bold text-emerald-800">
          Abrir suporte <ArrowRightIcon className="h-4 w-4" />
        </Link>
      </div>

      <div className="grid gap-4 p-6 md:grid-cols-[.8fr_.8fr_1.4fr]">
        <div className="rounded-2xl bg-slate-50 p-4">
          <ClockIcon className="h-5 w-5 text-amber-600" />
          <strong className="mt-3 block text-2xl text-slate-950">{loading ? "—" : waitingForTeam}</strong>
          <span className="text-sm text-slate-500">A aguardar a administração</span>
        </div>
        <div className={`rounded-2xl p-4 ${waitingForCustomer ? "bg-violet-50" : "bg-slate-50"}`}>
          <ExclamationCircleIcon className={`h-5 w-5 ${waitingForCustomer ? "text-violet-700" : "text-slate-500"}`} />
          <strong className="mt-3 block text-2xl text-slate-950">{loading ? "—" : waitingForCustomer}</strong>
          <span className="text-sm text-slate-500">Precisam da sua resposta</span>
        </div>
        <div className="rounded-2xl border border-slate-100 p-4">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Pedidos recentes</p>
          {loading ? (
            <p className="mt-4 text-sm text-slate-500">A atualizar…</p>
          ) : recent.length ? (
            <div className="mt-3 divide-y divide-slate-100">
              {recent.map((ticket) => (
                <Link key={ticket.id} to="/support" className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
                  <ChatBubbleLeftRightIcon className="h-5 w-5 shrink-0 text-slate-400" />
                  <span className="min-w-0 flex-1 truncate text-sm font-bold text-slate-800">{ticket.subject}</span>
                  <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold ${STATUS_STYLES[ticket.status] || STATUS_STYLES.OPEN}`}>
                    {STATUS_LABELS[ticket.status] || ticket.status}
                  </span>
                </Link>
              ))}
            </div>
          ) : (
            <div className="mt-4 flex items-center justify-between gap-3">
              <p className="text-sm text-slate-500">Ainda não criou nenhum pedido.</p>
              <Link to="/support" className="shrink-0 text-sm font-bold text-emerald-800">Pedir ajuda</Link>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
